import { Form, Button } from "react-bootstrap";
import '../../assets/css/resetPassword.scss';
import { useEffect, useState } from "react";
import { Eye, EyeOff } from 'lucide-react';
import { useDispatch } from "react-redux";
import { postResetPasswordData } from "../redux/action";
import * as HeaderAuthentication from '../../components/HeaderAuthentication';
import { useLocation, useNavigate } from "react-router-dom";
import failedImg from '../../assets/images/failImg.png'
import successImg from '../../assets/images/successImg.png';

export default function ResetPasswordComponent({ onHide }) {
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const signInDetails = JSON.parse(sessionStorage.getItem("SignInDetails"));
    const email = signInDetails?.[0]?.email;

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");

    const [errorNewPassword, setErrorNewPassword] = useState("");
    const [errorConfirmPassword, setErrorConfirmPassword] = useState("");
    const [isFocusedOrTyping, setIsFocusedOrTyping] = useState(false);

    const [tokenEmail, setTokenEmail] = useState("");
    const [tokenError, setTokenError] = useState("");
    const [isExpired, setIsExpired] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [successMessage, setSuccessMessage] = useState("");

    function decodeResetToken(token, secretKey = 'lm_pswd_key_226') {
        try {
            const decodedBinary = atob(token);
            const decryptedBytes = new Uint8Array(decodedBinary.length);
            for (let i = 0; i < decodedBinary.length; i++) {
                const keyChar = secretKey.charCodeAt(i % secretKey.length);
                decryptedBytes[i] = decodedBinary.charCodeAt(i) ^ keyChar;
            }
            const decoder = new TextDecoder('utf-8');
            const decrypted = decoder.decode(decryptedBytes);
            const [email, timestamp] = decrypted.split('|');
            if (!email || !timestamp || timestamp.length !== 14) {
                throw new Error("Invalid format");
            }
            const year = parseInt(timestamp.slice(0, 4));
            const month = parseInt(timestamp.slice(4, 6)) - 1;
            const day = parseInt(timestamp.slice(6, 8));
            const hour = parseInt(timestamp.slice(8, 10));
            const minute = parseInt(timestamp.slice(10, 12));
            const second = parseInt(timestamp.slice(12, 14));
            const expiryDate = new Date(year, month, day, hour, minute, second);
            const now = new Date();
            return {
                email,
                expiryDate,
                // isExpired: now > expiryDate
                isExpired: now < expiryDate
            };
        } catch (error) {
            return {
                error: 'Invalid or corrupted token',
                details: error.message
            };
        }
    }

    const queryParams = new URLSearchParams(location.search);
    const token = queryParams.get("token");

    useEffect(() => {
        if (token) {
            const result = decodeResetToken(token);
            if (result.error) {
                setTokenError(result.error);
            } else {
                setIsExpired(result.isExpired);
                if (!result.isExpired) {
                    setTokenEmail(result.email);
                } else {
                    setErrorMessage("Reset link has expired.");
                }
            }
        } else {
            setTokenError("Missing token in URL");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    // Password rule checks
    const hasMinLength = newPassword.length >= 8;
    const hasUpperCase = /[A-Z]/.test(newPassword);
    const hasLowerCase = /[a-z]/.test(newPassword);
    const hasNumber = /[0-9]/.test(newPassword);
    const hasSymbol = /[!@#$%^&*(),.?":{}|<>]/.test(newPassword);

    const handleResetPassword = (e) => {
        e.preventDefault();

        let valid = true;
        setErrorNewPassword("");
        setErrorConfirmPassword("");

        if (!hasMinLength) {
            setErrorNewPassword("Password must be at least 8 characters.");
            valid = false;
        }

        if (newPassword !== confirmPassword) {
            setErrorConfirmPassword("Passwords do not match.");
            valid = false;
        }

        if (!valid) return;

        const requestPayload = {
            email: token ? tokenEmail : email,
            new_password: newPassword,
        };

        const requestOption = {
            method: 'POST',
            mode: 'cors',
            headers: HeaderAuthentication.postAuthOfLeadMerge,
            body: JSON.stringify(requestPayload),
        };

        dispatch(postResetPasswordData(requestOption, setSuccessMessage, setErrorMessage));
        console.log('Reset Password Request:', requestPayload);
    };

    const getTextClass = (condition) => {
        if (!isFocusedOrTyping) return '';
        return condition ? 'text-success' : 'text-dangerous';
    };

    const canSubmit = hasMinLength && hasUpperCase && hasLowerCase && hasNumber && hasSymbol;

    const handleFails = () => {
        navigate('/forget-password')
    }

    const handleSuccess = () => {
        if (onHide) {
            onHide();
        } else if (token) {
            navigate('/')
        } else {
            navigate(-1)
        }
    }

    return (

        <div className="reset-box">
            {(!isExpired && !successMessage) && (<>
                <h2><b>Reset password</b></h2>
                <p className="subtext">Just type it twice and try not to forget it.<br />Password should be and must contain:</p>
                <div className="password-rules-box d-flex justify-content-between flex-wrap">
                    <div className="rule-box">
                        <div className={getTextClass(hasMinLength)}>8+</div>
                        <span className={getTextClass(hasMinLength)}>Character</span>
                    </div>
                    <div className="rule-box">
                        <div className={getTextClass(hasUpperCase)}>AA</div>
                        <span className={getTextClass(hasUpperCase)}>Uppercase</span>
                    </div>
                    <div className="rule-box">
                        <div className={getTextClass(hasLowerCase)}>aa</div>
                        <span className={getTextClass(hasLowerCase)}>Lowercase</span>
                    </div>
                    <div className="rule-box">
                        <div className={getTextClass(hasNumber)}>123</div>
                        <span className={getTextClass(hasNumber)}>Number</span>
                    </div>
                    <div className="rule-box">
                        <div className={getTextClass(hasSymbol)}>@#$</div>
                        <span className={getTextClass(hasSymbol)}>Symbol</span>
                    </div>
                </div>

                <Form className="mt-4" onSubmit={handleResetPassword}
                    style={{ width: "100%" }}>
                    <Form.Group className="mb-3">
                        <Form.Label>New Password</Form.Label>
                        <div className="password-input-wrapper">
                            <Form.Control
                                type={showPassword ? "text" : "password"}
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                onFocus={() => setIsFocusedOrTyping(true)}
                                onBlur={() => setIsFocusedOrTyping(false)}
                            />
                            <span
                                className="eye-icon"
                                onClick={() => setShowPassword(prev => !prev)}
                            >
                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </span>
                        </div>
                        {errorNewPassword && <div className="text-danger mt-1">{errorNewPassword}</div>}
                    </Form.Group>
                    <Form.Group className="mb-4">
                        <Form.Label>Confirm Password</Form.Label>
                        <div className="password-input-wrapper">
                            <Form.Control
                                type={showConfirmPassword ? "text" : "password"}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                            />
                            <span
                                className="eye-icon"
                                onClick={() => setShowConfirmPassword(prev => !prev)}
                            >
                                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </span>
                        </div>
                        {errorConfirmPassword && <div className="text-danger mt-1">{errorConfirmPassword}</div>}
                    </Form.Group>
                    <Button className="reset-btn" type="submit" disabled={!canSubmit}>Reset Password</Button>
                </Form>
            </>)}
            {isExpired && errorMessage && (
                <div className="mt-3 d-flex flex-column justify-content-center align-items-center">
                    <h2><b>Failed!</b></h2>
                    <p className="text-danger">{errorMessage}</p>
                    <img src={failedImg} alt="failed" className="p-4 success-image" />
                    <Button className="reset-btn" onClick={handleFails}>Re-Send</Button>
                </div>
            )}

            {/* Show success message if any */}
            {successMessage && (
                <div className="mt-3 d-flex flex-column justify-content-center align-items-center">
                    <h2><b>Congratulations !</b></h2>
                    <p className="text-success">{successMessage}</p>
                    <img src={successImg} alt="success" className="p-4 success-image" />
                    <Button className="reset-btn" onClick={handleSuccess}>{onHide ? "Close" : token ? 'Sign In' : 'Back'}</Button>
                </div>
            )}
        </div>
    );
};
